import { Link, Table } from '@chakra-ui/react';
import {
  AccountStatusEnum,
  AssociationEntity,
  IAccountRespSchema,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import startCase from 'lodash/startCase';
import { useMemo } from 'react';
import { MdOpenInNew } from 'react-icons/md';
import { useGetAssociationsByExternalSystem } from '../../../api/associationsService';
import { AccountSelect } from '../../../components/Account/AccountSelect';
import { MFormField, MIcon } from '../../../components/Monetize';
import { getAccountDetailRoute } from '../../../constants/routes';
import {
  useRelatedRecordValues,
  useTableRowValues,
} from './AccountMerge.hooks';
import {
  KeepAccountRadioOption,
  ThData,
  TrData,
} from './AccountMergeRenderers';
import { AccountToKeep } from './AccountMergeTable.types';
import { CustomFieldTableRows } from './CustomFieldTableRows';
import { RelatedRecordsSummary } from './RelatedRecordTableRows';

export function AccountMergeComparisonTable({
  mainAccount,
  otherAccount,
  accountToKeep,
  onOtherAccountChange,
  onAccountToKeepChange,
  onRelatedValuesChange,
}: {
  mainAccount: IAccountRespSchema;
  otherAccount?: Maybe<IAccountRespSchema>;
  accountToKeep: AccountToKeep;
  onOtherAccountChange: (account: IAccountRespSchema) => void;
  onAccountToKeepChange: (accountToKeep: AccountToKeep) => void;
  onRelatedValuesChange: (
    retainPaymentGatewayAccountOptions: ReturnType<
      typeof useRelatedRecordValues
    >,
  ) => void;
}) {
  const { data: sourceAssociations } = useGetAssociationsByExternalSystem({
    entity: AssociationEntity.ACCOUNT,
    recordId: mainAccount.id,
  });

  const { data: targetAssociations } = useGetAssociationsByExternalSystem(
    { entity: AssociationEntity.ACCOUNT, recordId: otherAccount?.id ?? '' },
    { enabled: !!otherAccount },
  );

  const associationKeys = useMemo(
    () =>
      Array.from(
        new Set([
          ...Object.keys(sourceAssociations || {}),
          ...Object.keys(targetAssociations || {}),
        ]),
      ),
    [sourceAssociations, targetAssociations],
  );

  const values = useTableRowValues(mainAccount, otherAccount);

  return (
    <Table.ScrollArea w="100%">
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row _hover={{ bg: 'transparent' }}>
            <Table.ColumnHeader
              width="200px"
              borderRight="1px solid {colors.tGray.back}"
            />
            <TrData borderRight="1px solid {colors.tGray.back}">
              <KeepAccountRadioOption
                value={AccountToKeep.MAIN}
                selectedValue={accountToKeep}
                onChecked={() => onAccountToKeepChange(AccountToKeep.MAIN)}
              />
            </TrData>
            <TrData omitWrap>
              <KeepAccountRadioOption
                value={AccountToKeep.OTHER}
                selectedValue={accountToKeep}
                onChecked={() => onAccountToKeepChange(AccountToKeep.OTHER)}
              />
            </TrData>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row _hover={{ fontWeight: '600' }}>
            <ThData>Account</ThData>
            <TrData
              cellStyle={
                accountToKeep === AccountToKeep.MAIN ? 'KEEP' : 'DELETE'
              }
              borderRight="1px solid {colors.tGray.back}"
            >
              {mainAccount.accountName}
            </TrData>
            <TrData
              cellStyle={
                otherAccount
                  ? accountToKeep === AccountToKeep.OTHER
                    ? 'KEEP'
                    : 'DELETE'
                  : 'NONE'
              }
              omitWrap
            >
              <MFormField label="Merge With" isRequired>
                <AccountSelect
                  mb={1}
                  clearable={false}
                  onChange={(event) => onOtherAccountChange(event)}
                  value={otherAccount?.id}
                  additionalSearchParams={{
                    id: `ne:${mainAccount.id}`,
                    status: `in:${[
                      AccountStatusEnum.ACTIVE,
                      AccountStatusEnum.INACTIVE,
                      AccountStatusEnum.CANCELED,
                      AccountStatusEnum.SUSPENDED,
                    ].join(',')}`,
                  }}
                  returnItem
                />
              </MFormField>
            </TrData>
          </Table.Row>
          <Table.Row _hover={{ fontWeight: '600' }}>
            <ThData>Account ID</ThData>
            <TrData
              cellStyle={
                accountToKeep === AccountToKeep.MAIN ? 'KEEP' : 'DELETE'
              }
              borderRight="1px solid {colors.tGray.back}"
            >
              <Link
                href={getAccountDetailRoute(mainAccount.id)}
                target="_blank"
                display="flex"
                alignItems="center"
              >
                {mainAccount.id}
                <MIcon as={MdOpenInNew} boxSize={4} ml={1} />
              </Link>
            </TrData>
            <TrData
              cellStyle={
                otherAccount
                  ? accountToKeep === AccountToKeep.OTHER
                    ? 'KEEP'
                    : 'DELETE'
                  : 'NONE'
              }
            >
              {otherAccount ? (
                <Link
                  href={getAccountDetailRoute(otherAccount.id)}
                  target="_blank"
                  display="flex"
                  alignItems="center"
                >
                  {otherAccount.id}
                  <MIcon as={MdOpenInNew} boxSize={4} ml={1} />
                </Link>
              ) : null}
            </TrData>
          </Table.Row>
          {associationKeys.map((key) => (
            <Table.Row key={key} _hover={{ fontWeight: '600' }}>
              <ThData>{`${startCase(key)} ID`}</ThData>
              <TrData
                blankRenderer={() => null}
                cellStyle={
                  accountToKeep === AccountToKeep.MAIN ? 'KEEP' : 'DELETE'
                }
                borderRight="1px solid {colors.tGray.back}"
              >
                {sourceAssociations?.[key]?.externalObjectId}
              </TrData>
              <TrData
                blankRenderer={() => null}
                cellStyle={
                  otherAccount
                    ? accountToKeep === AccountToKeep.OTHER
                      ? 'KEEP'
                      : 'DELETE'
                    : 'NONE'
                }
              >
                {targetAssociations?.[key]?.externalObjectUrl ? (
                  <Link
                    href={targetAssociations?.[key].externalObjectUrl}
                    target="_blank"
                    display="flex"
                    alignItems="center"
                  >
                    {targetAssociations?.[key]?.externalObjectId}
                    <MIcon as={MdOpenInNew} boxSize={4} ml={1} />
                  </Link>
                ) : (
                  targetAssociations?.[key]?.externalObjectId
                )}
              </TrData>
            </Table.Row>
          ))}
          {values.map(({ label, account1Value, account2Value }) => (
            <Table.Row key={label} _hover={{ fontWeight: '600' }}>
              <ThData>{label}</ThData>
              <TrData
                cellStyle={
                  accountToKeep === AccountToKeep.MAIN ? 'KEEP' : 'DELETE'
                }
                borderRight="1px solid {colors.tGray.back}"
              >
                {account1Value}
              </TrData>
              <TrData
                cellStyle={
                  otherAccount
                    ? accountToKeep === AccountToKeep.OTHER
                      ? 'KEEP'
                      : 'DELETE'
                    : 'NONE'
                }
              >
                {account2Value}
              </TrData>
            </Table.Row>
          ))}
          <CustomFieldTableRows
            mainAccount={mainAccount}
            otherAccount={otherAccount}
            accountToKeep={accountToKeep}
          />
          <RelatedRecordsSummary
            mainAccount={mainAccount}
            otherAccount={otherAccount}
            accountToKeep={accountToKeep}
            onRelatedValuesChange={onRelatedValuesChange}
          />
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
}
